import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./AdminScheduling.css";

import WeekBar from "./components/WeekBar";
import MasterScheduleView from "./components/MasterScheduleView";
import DaySection from "./components/DaySection";
import CreateSessionModal from "./components/CreateSessionModal";
import EditSessionModal from "./components/EditSessionModal";
import DayEditModal from "./components/DayEditModal"; 
import DuplicateMenuModal from "./components/DuplicateMenuModal";
import MasterDaySettingsModal from "./components/MasterDaySettingsModal";
import TrashModal from "./components/TrashModal";
import CreateEventModal from "./components/CreateEventModal";
import EditEventModal from "./components/EditEventModal";

import { DAYS_ORDER, WEEKDAY_BLOCKS, SATURDAY_BLOCKS } from "./constants"; 
import { addDays, clampDateToMidnight, isSameDay, startOfWeekMonday, minutesToDate } from "./dateUtils";
import { dateKeyLocal } from "./dayConfigUtils"; 
import {
  fetchActiveTutors,
  fetchAllStudents,
  fetchAvailabilityBlocks,
  upsertAvailabilityByDate,
  fetchScheduledSessions,
  fetchTutorClockIntervals,
  adjustClockInterval,
  updateSessionStatus,
  createScheduledSession,
  updateScheduledSession,
  deleteScheduledSession,
  fetchSessionsForStudentRange,
  fetchDayConfigsForDates, 
  upsertDayConfigByDate, 
  batchDeleteSessionsByIds,
  batchUpdateSessions,
  fetchTutorOrder,
  saveTutorOrder,
  duplicateScheduleRangeToTargetWeek,
  fetchMasterScheduleSessions,
  createMasterScheduleSession,
  updateMasterScheduleSession,
  deleteMasterScheduleSession,
  duplicateMasterScheduleToWeek,
  fetchAllMasterAvailability, 
  upsertMasterAvailability, 
  fetchMasterDayConfig,
  upsertMasterDayConfig,
  fetchDefaultScheduleWeekConfig,
} from "./schedulingApi";
import { fetchSessionLogsBySessionIds } from "../../tutor/session_logs/sessionLogApi";
import {
  buildSessionNotificationsBySessionId,
  fetchDiagnosticNotificationsByStudentId,
  SCHEDULE_NOTIFICATION_WINDOWS,
} from "./notifications/sessionNotifications";

import { db } from "../../../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
// ScheduleActivityToast is now mounted globally in AdminDashboard
import { SESSIONS_COL } from "./schedulingApi";

import SchedulingSettingsModal from "./components/SchedulingSettingsModal";
import SchedulingSettingsHub from "./components/SchedulingSettingsHub";
import useAdminScheduleHotkeys from "./useAdminScheduleHotkeys";
import { loadClipboard, saveClipboard } from "./clipboardStore";
import {
  dayKeyFromDate,
  defaultTimelineRangeForDayKey as defaultScheduleTimelineRangeForDayKey,
  resolveScheduleForDate,
  setActiveDefaultWeekConfig,
} from "./scheduleSettingsUtils";
import ScheduleCalendarView from "./schedule_calendar/ScheduleCalendarView";
import {
  SESSION_ANIMATION_TIMINGS,
  applyPatchPreview,
  buildSessionAnimationCssVars,
  isPlacementPatch,
  resolvePlacementTarget,
  waitForMs,
} from "./sessionAnimations";
import { useDeveloperModeFlag } from "./developerModeTools";
import { createEvent, updateEvent, deleteEvent, subscribeToEventsForRange } from "./eventsApi";
import { computeEventsForDate } from "./eventUtils";
import ScheduleMonthView from "./components/ScheduleMonthView";
import WeekEventHeader from "./components/WeekEventHeader";
import { buildScheduleRoute, parseScheduleRoute } from "./scheduleRouteState";
import { resolveCopiedStudentGrade } from "./sessionCopyUtils";

// ---- Month/Year cursor helpers (local, no deps) ----
function startOfMonthLocal(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfYearLocal(d) {
  return new Date(d.getFullYear(), 0, 1);
}

function dayKeyForScheduleDate(d) {
  return dayKeyFromDate(d);
}

const UI_SETTINGS_STORAGE_KEY = "adminSchedulingUiSettings_v1";
const UI_SETTINGS_DOC = { col: "settings", id: "admin_scheduling_ui" };
const DEFAULT_UI_SETTINGS = {
  defaultOpenView: "week",
  timeSlotLayout: "one",
  showWeekBarByDefault: true,
};
const DEV_CLOCK_EDIT_EMAILS = (process.env.REACT_APP_CLOCK_EDIT_ALLOWED_EMAILS || "teproject580@gmail.com")
  .split(",")
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);
const DEV_CLOCK_EDIT_SECRET = (process.env.REACT_APP_CLOCK_EDIT_SECRET || "").trim();


function addMonthsLocal(d, delta) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function getDayKeyForDate(d) {
  return dayKeyFromDate(d);
}

function formatDateRangeLabel(startDate) {
  const start = clampDateToMidnight(startDate);
  const end = addDays(start, 6);
  return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
}

function addWeekOffset(baseWeekStart, offsetWeeks) {
  return addDays(baseWeekStart, offsetWeeks * 7);
}

function defaultBlocksForDayKey(dayKey) {
  return dayKey === "saturday" ? SATURDAY_BLOCKS : WEEKDAY_BLOCKS;
}

function defaultTimelineRangeForDayKey(dayKey) {
  return defaultScheduleTimelineRangeForDayKey(dayKey);
}

function resolveTimelineRangeForDay(dayKey, config = {}) {
  const defaults = defaultTimelineRangeForDayKey(dayKey);
  const rawStart = Number(config?.timelineStartMin);
  const rawEnd = Number(config?.timelineEndMin);

  if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || rawEnd <= rawStart) {
    return defaults;
  }

  const startMin = Math.max(0, Math.min(23 * 60 + 55, rawStart));
  const endMin = Math.max(startMin + 60, Math.min(24 * 60, rawEnd));
  return { startMin, endMin };
}

function normalizeStatusValue(status) {
  const v = String(status || "Pending").trim().toLowerCase();
  if (v === "cancelled" || v === "canceled") return "canceled";
  if (v === "confirmed") return "confirmed";
  if (v === "present") return "present";
  return "pending";
}

function isCanceledStatus(status) {
  return normalizeStatusValue(status) === "canceled";
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const asNum = Number(value);
  if (Number.isFinite(asNum)) return asNum;
  return 0;
}

function pickLatestAvailabilityRecord(records = []) {
  if (!Array.isArray(records) || records.length === 0) return null;
  return records.reduce((latest, rec) => {
    if (!latest) return rec;
    return timestampToMillis(rec?.updatedAt) > timestampToMillis(latest?.updatedAt) ? rec : latest;
  }, null);
}

function resolveAvailabilityForDay(record, dayKey) {
  const defaults = defaultTimelineRangeForDayKey(dayKey);
  if (!record) {
    return {
      startMin: defaults.startMin,
      endMin: defaults.endMin,
      unavailable: true,
      hidden: false,
    };
  }

  const fromDateStart =
    record?.start instanceof Date
      ? record.start.getHours() * 60 + record.start.getMinutes()
      : defaults.startMin;
  const fromDateEnd =
    record?.end instanceof Date
      ? record.end.getHours() * 60 + record.end.getMinutes()
      : defaults.endMin;

  const rawStart = Number(record?.startMin);
  const rawEnd = Number(record?.endMin);
  const startMin = Number.isFinite(rawStart) ? rawStart : fromDateStart;
  const endMin =
    Number.isFinite(rawEnd) && rawEnd > startMin ? rawEnd : Math.max(startMin + 60, fromDateEnd, defaults.endMin);

  return {
    startMin,
    endMin,
    unavailable: !!record?.unavailable,
    hidden: !!record?.hidden,
  };
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function toMidnightMillis(value) {
  if (!value) return NaN;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return NaN;
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function minuteOfDay(value) {
  const d = value instanceof Date ? value : new Date(value);
  return d.getHours() * 60 + d.getMinutes();
}

function shouldShowClockIndicatorsForTutor(tutor) {
  const role = String(tutor?.role || "").toLowerCase();
  return role !== "admin";
}

function parseClockEditInput(baseDate, rawInput) {
  const value = String(rawInput || "").trim();
  if (!value) return null;

  let match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        hour,
        minute,
        0,
        0
      );
    }
  }

  match = value.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
  if (match) {
    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const ampm = match[3].toUpperCase();
    if (hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59) {
      if (ampm === "AM") {
        if (hour === 12) hour = 0;
      } else if (hour !== 12) {
        hour += 12;
      }

      return new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        baseDate.getDate(),
        hour,
        minute,
        0,
        0
      );
    }
  }

  return null;
}

function resolveLaneForRange(existingSessions, {
  excludeSessionId = null,
  tutorUid,
  date,
  start,
  end,
  preferredLane = null,
}) {
  if (!tutorUid || !date || !start || !end) return null;

  const candidateStartMin = minuteOfDay(start);
  const candidateEndMin = minuteOfDay(end);
  if (candidateEndMin <= candidateStartMin) return null;

  const sessions = (existingSessions || [])
    .filter((session) => {
      if (excludeSessionId && session.id === excludeSessionId) return false;
      if (session.tutorUid !== tutorUid) return false;
      if (!isSameDay(new Date(session.start), date)) return false;
      if (isCanceledStatus(session.status)) return false;

      const startMin = minuteOfDay(session.start);
      const endMin = Math.max(startMin + 5, minuteOfDay(session.end));
      return overlaps(startMin, endMin, candidateStartMin, candidateEndMin);
    })
    .map((session) => ({
      id: session.id,
      startMin: minuteOfDay(session.start),
      endMin: Math.max(minuteOfDay(session.start) + 5, minuteOfDay(session.end)),
    }));

  sessions.push({
    id: "__candidate__",
    startMin: candidateStartMin,
    endMin: candidateEndMin,
  });

  sessions.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });

  const laneEnds = [-Infinity, -Infinity, -Infinity];

  for (const session of sessions) {
    const freeLanes = [];
    for (let idx = 0; idx < 3; idx += 1) {
      if (laneEnds[idx] <= session.startMin) freeLanes.push(idx);
    }

    let lane = null;
    if (session.id === "__candidate__" && Number.isInteger(preferredLane) && freeLanes.includes(preferredLane)) {
      lane = preferredLane;
    } else if (freeLanes.length > 0) {
      lane = freeLanes[0];
    }

    if (lane == null) {
      if (session.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], session.endMin);

    if (session.id === "__candidate__") {
      return lane;
    }
  }

  return null;
}

function isSameRouteDate(a, b) {
  if (!(a instanceof Date) || !(b instanceof Date)) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function visualOrderOfSession(session) {
  const raw = Number(session?.visualOrder);
  if (Number.isFinite(raw)) return raw;
  if (Number.isInteger(session?.slotIdx)) return session.slotIdx;
  return 0;
}

export default function AdminScheduling() {
  const location = useLocation();
  const navigate = useNavigate();
  const today = useMemo(() => clampDateToMidnight(new Date()), []);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(today));
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);

  const [availability, setAvailability] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [clockIntervals, setClockIntervals] = useState([]);
  const [sessionLogBySessionId, setSessionLogBySessionId] = useState({});
  const [sessionAnimationById, setSessionAnimationById] = useState({});

  const [isMasterMode, setIsMasterMode] = useState(false);
  const {
    isDeveloperMode,
  } = useDeveloperModeFlag();
  const [masterSessions, setMasterSessions] = useState([]);
  const [masterRefreshKey, setMasterRefreshKey] = useState(0);
  const [masterTargetWeekStart, setMasterTargetWeekStart] = useState(startOfWeekMonday(new Date()));

  const [selectedTutorUid, setSelectedTutorUid] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const [dayConfigs, setDayConfigs] = useState({});
  const [dayEdit, setDayEdit] = useState(null); // { dayKey, date }

  const [masterAvailability, setMasterAvailability] = useState([]);
  const [masterDayConfigs, setMasterDayConfigs] = useState({});
  const [masterDayEditKey, setMasterDayEditKey] = useState(null); // "monday", "tuesday"...

  const [tutorOrder, setTutorOrder] = useState([]); 
  
  const [viewMode, setViewMode] = useState("week");
  const [dupModal, setDupModal] = useState(null);

  // NEW State for Trash Modal
  const [isTrashModalOpen, setIsTrashModalOpen] = useState(false);
  const [diagnosticNotificationsByStudentId, setDiagnosticNotificationsByStudentId] = useState({});

  // Events feature state
  const [events, setEvents] = useState([]);
  const [createEventCtx, setCreateEventCtx] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  const studentsById = useMemo(() => {
    const map = {};
    for (let i = 0; i < (students || []).length; i += 1) {
      const student = students[i];
      if (!student?.id) continue;
      map[student.id] = student;
    }
    return map;
  }, [students]);

  const visibleStudentIds = useMemo(() => {
    const ids = new Set();
    for (let i = 0; i < (sessions || []).length; i += 1) {
      const studentId = sessions[i]?.studentId;
      if (studentId) ids.add(studentId);
    }
    return [...ids];
  }, [sessions]);

  const visibleStudents = useMemo(
    () => visibleStudentIds.map((studentId) => studentsById[studentId]).filter(Boolean),
    [visibleStudentIds, studentsById]
  );

  const sessionNotificationsBySessionId = useMemo(() => {
    return buildSessionNotificationsBySessionId({
      sessions,
      studentsById,
      diagnosticNotificationsByStudentId,
      conferenceActiveDays: SCHEDULE_NOTIFICATION_WINDOWS.conferenceDays,
      now: new Date(),
    });
  }, [sessions, studentsById, diagnosticNotificationsByStudentId]);

  // UI sizing (Zoom) — persisted so it survives refresh
  const ZOOM_STORAGE_KEY = "adminSchedulingZoom";
  const [uiZoom, setUiZoom] = useState(() => {
    try {
      const raw = localStorage.getItem(ZOOM_STORAGE_KEY);
      const n = parseFloat(raw);
      if (Number.isFinite(n)) return Math.min(1, Math.max(0.75, n));
    } catch (e) {}
    return 1;
  });

  useEffect(() => {
    try {
      localStorage.setItem(ZOOM_STORAGE_KEY, String(uiZoom));
    } catch (e) {}
  }, [uiZoom]);

  const isPastDate = useCallback((value) => {
    const dayMs = toMidnightMillis(value);
    const todayMs = toMidnightMillis(new Date());
    if (!Number.isFinite(dayMs) || !Number.isFinite(todayMs)) return false;
    return dayMs < todayMs;
  }, []);

  const isPastDayLocked = useCallback(
    (value) => !isMasterMode && !isDeveloperMode && isPastDate(value),
    [isMasterMode, isDeveloperMode, isPastDate]
  );

  const ensureDateIsEditable = useCallback(
    (value) => {
      if (!isPastDayLocked(value)) return true;
      return false;
    },
    [isPastDayLocked]
  );

  // ---- UI Settings (Default open view + time slot layout) ----
  const [uiSettings, setUiSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(UI_SETTINGS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        defaultOpenView: parsed?.defaultOpenView || DEFAULT_UI_SETTINGS.defaultOpenView,
        timeSlotLayout: parsed?.timeSlotLayout || DEFAULT_UI_SETTINGS.timeSlotLayout,
        showWeekBarByDefault:
          typeof parsed?.showWeekBarByDefault === "boolean"
            ? parsed.showWeekBarByDefault
            : DEFAULT_UI_SETTINGS.showWeekBarByDefault,
      };
    } catch (e) {
      return { ...DEFAULT_UI_SETTINGS };
    }
  });

  const [isSettingsOpen, setIsSettingsOpen] = useState(false); // settings hub page
  const [isScheduleCalendarOpen, setIsScheduleCalendarOpen] = useState(false);
  const [isTutorOrderOpen, setIsTutorOrderOpen] = useState(false);
  const [hasLoadedUiSettings, setHasLoadedUiSettings] = useState(false);
  const [hasHydratedDayConfigs, setHasHydratedDayConfigs] = useState(false);
  const userChangedViewRef = useRef(false);
  const initialViewAppliedRef = useRef(false);
  const initialDrawerPreferenceAppliedRef = useRef(false);

  const [monthCursor, setMonthCursor] = useState(() => startOfMonthLocal(today));
  const [yearCursor, setYearCursor] = useState(() => startOfYearLocal(today));
  const [monthSelectedDate, setMonthSelectedDate] = useState(() => clampDateToMidnight(new Date()));
  // Incrementing counter passed to ScheduleMonthView to force scroll-to-today
  // even when monthCursor value doesn't change (user already on today's month).
  const [monthScrollTrigger, setMonthScrollTrigger] = useState(0);

  // Modal state
  const [createCtx, setCreateCtx] = useState(null);
  const [editSession, setEditSession] = useState(null);

  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); 
  const selectedSessionIdRef = useRef(null);
  const selectedSlotRef = useRef(null);
  const [clipboard, setClipboard] = useState(() => loadClipboard());
  const wrapRef = useRef(null);
  const scheduleRouteState = useMemo(
    () => parseScheduleRoute(location.pathname, location.search),
    [location.pathname, location.search]
  );
  const routeApplyInFlightRef = useRef(null);

  const dayRefs = useRef({});
  const animationTimerRef = useRef(new Map());
  const animationCssVars = useMemo(
    () => buildSessionAnimationCssVars(SESSION_ANIMATION_TIMINGS),
    []
  );

  // Smoothly scroll the scheduling container to a day section.
  // Accounts for both the sticky WeekBar and the sticky WeekEventHeader heights.
  const scrollToDayKey = useCallback((dayKey, padding = 12) => {
    const container = wrapRef.current;
    const node = dayRefs.current[dayKey];
    if (!container || !node) return;

    const wb  = container.querySelector(".weekBarWrapper");
    const wbH = wb  ? wb.getBoundingClientRect().height  : 0;
    // Also subtract the WeekEventHeader height if it's visible
    const weh  = container.querySelector(".weh-root");
    const wehH = weh ? weh.getBoundingClientRect().height : 0;

    const cRect = container.getBoundingClientRect();
    const nRect = node.getBoundingClientRect();
    const targetTop = container.scrollTop + (nRect.top - cRect.top) - wbH - wehH - padding;

    container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  }, []);

  // Scroll to a day section while staying in Week view (used by WeekEventHeader day clicks).
  // If the dayKey has no rendered DaySection (e.g. the off-week Sunday), does nothing.
  const scrollToWeekDay = useCallback((dayKey) => {
    if (!dayKey || !dayRefs.current[dayKey]) return;
    scrollToDayKey(dayKey, 16);
  }, [scrollToDayKey]);

  const resetScheduleScroll = useCallback(() => {
    if (wrapRef.current) {
      wrapRef.current.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, []);

  useEffect(() => {
    if (!wrapRef.current) return;
    if (viewMode === "week" || DAYS_ORDER.includes(viewMode)) {
      resetScheduleScroll();
    }
  }, [viewMode, weekStart, resetScheduleScroll]);

  const clearSessionAnimation = useCallback((sessionId) => {
    if (!sessionId) return;
    const existingTimer = animationTimerRef.current.get(sessionId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      animationTimerRef.current.delete(sessionId);
    }
    setSessionAnimationById((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, sessionId)) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
  }, []);

  const setSessionAnimation = useCallback((sessionId, phase, ttlMs = SESSION_ANIMATION_TIMINGS.animationRetentionMs) => {
    if (!sessionId || !phase) return;
    const existingTimer = animationTimerRef.current.get(sessionId);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      animationTimerRef.current.delete(sessionId);
    }

    setSessionAnimationById((prev) => ({ ...prev, [sessionId]: phase }));

    const timer = window.setTimeout(() => {
      setSessionAnimationById((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, sessionId)) return prev;
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
      animationTimerRef.current.delete(sessionId);
    }, Math.max(80, Number(ttlMs) || SESSION_ANIMATION_TIMINGS.animationRetentionMs));

    animationTimerRef.current.set(sessionId, timer);
  }, []);

  useEffect(() => {
    const timers = animationTimerRef.current;
    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const isDateClosed = useCallback((dateObj) => {
    if (!(dateObj instanceof Date)) return false;
    const key = dateKeyLocal(dateObj);
    const cfg = dayConfigs[key];
    return !!resolveScheduleForDate(dateObj, cfg).isClosed;
  }, [dayConfigs]);

  const resolveTodayOrNextOpenDate = useCallback((baseDate = new Date()) => {
    let searchDate = clampDateToMidnight(baseDate);
    let foundKey = dayKeyForScheduleDate(searchDate);
    let daysChecked = 0;

    while ((!foundKey || isDateClosed(searchDate)) && daysChecked < 7) {
      searchDate = addDays(searchDate, 1);
      foundKey = dayKeyForScheduleDate(searchDate);
      daysChecked += 1;
    }

    return {
      date: searchDate,
      dayKey: foundKey || DAYS_ORDER[0],
    };
  }, [isDateClosed]);

  const applyViewFromSettings = useCallback(
    (settings) => {
      const pref = settings?.defaultOpenView || "week";

      if (pref === "day") {
        const target = resolveTodayOrNextOpenDate(new Date());
        // CRITICAL: If the search moved us to next week (e.g., Today is Sunday → Monday),
        // update weekStart so the correct week renders.
        setWeekStart(startOfWeekMonday(target.date));
        setViewMode(target.dayKey);
        setTimeout(() => scrollToDayKey(target.dayKey, 12), 50);
        return;
      }

      if (pref === "week") {
        // Smart today: land on today or next open schedule day and scroll to it
        const target = resolveTodayOrNextOpenDate(new Date());
        setWeekStart(startOfWeekMonday(target.date));
        setViewMode("week");
        setTimeout(() => scrollToDayKey(target.dayKey, 12), 100);
        return;
      }

      // month / year: just set the view
      if (pref === "month" || pref === "year") {
        setViewMode(pref);
      }
    },
    [resolveTodayOrNextOpenDate, scrollToDayKey]
  );

  // Load global UI settings (Firestore) once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ref = doc(db, UI_SETTINGS_DOC.col, UI_SETTINGS_DOC.id);
        const snap = await getDoc(ref);
        if (!alive) return;

        if (snap.exists()) {
          const data = snap.data() || {};
          const currentUid = getAuth().currentUser?.uid || null;
          const perUserDrawerDefault = currentUid
            ? data?.weekBarDefaultOpenByUser?.[currentUid]
            : undefined;
          const next = {
            defaultOpenView: data.defaultOpenView || DEFAULT_UI_SETTINGS.defaultOpenView,
            timeSlotLayout: data.timeSlotLayout || DEFAULT_UI_SETTINGS.timeSlotLayout,
            showWeekBarByDefault:
              typeof perUserDrawerDefault === "boolean"
                ? perUserDrawerDefault
                : typeof data.showWeekBarByDefault === "boolean"
                  ? data.showWeekBarByDefault
                  : DEFAULT_UI_SETTINGS.showWeekBarByDefault,
          };
          setUiSettings(next);
          try {
            localStorage.setItem(UI_SETTINGS_STORAGE_KEY, JSON.stringify(next));
          } catch (e) {}
        }
      } catch (e) {
        console.warn("Failed to load admin scheduling UI settings:", e);
      } finally {
        if (alive) setHasLoadedUiSettings(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Apply initial view once, after UI settings are loaded and day configs are hydrated.
  // This prevents day-mode defaulting from using legacy closed-day fallbacks before configs arrive.
  useEffect(() => {
    if (initialViewAppliedRef.current) return;
    if (userChangedViewRef.current) return;
    if (!hasLoadedUiSettings) return;

    const pref = uiSettings?.defaultOpenView || "week";
    if (pref === "day" && !hasHydratedDayConfigs) return;

    applyViewFromSettings(uiSettings);
    initialViewAppliedRef.current = true;
  }, [uiSettings, hasLoadedUiSettings, hasHydratedDayConfigs, applyViewFromSettings]);

  useEffect(() => {
    if (initialDrawerPreferenceAppliedRef.current) return;
    if (!hasLoadedUiSettings) return;

    setIsDrawerOpen(!!uiSettings?.showWeekBarByDefault);
    initialDrawerPreferenceAppliedRef.current = true;
  }, [hasLoadedUiSettings, uiSettings]);

  const handleSetWeekBarDefaultVisibility = useCallback(async (shouldShowByDefault) => {
    const nextValue = !!shouldShowByDefault;

    // Reflect preference immediately in the live UI.
    setIsDrawerOpen(nextValue);

    setUiSettings((prev) => {
      const next = { ...prev, showWeekBarByDefault: nextValue };
      try {
        localStorage.setItem(UI_SETTINGS_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {}
      return next;
    });

    try {
      const uid = getAuth().currentUser?.uid;
      const ref = doc(db, UI_SETTINGS_DOC.col, UI_SETTINGS_DOC.id);

      if (uid) {
        await setDoc(ref, { updatedAt: Timestamp.now() }, { merge: true });
        await updateDoc(ref, {
          [`weekBarDefaultOpenByUser.${uid}`]: nextValue,
          updatedAt: Timestamp.now(),
        });
      } else {
        await setDoc(
          ref,
          {
            showWeekBarByDefault: nextValue,
            updatedAt: Timestamp.now(),
          },
          { merge: true }
        );
      }
    } catch (e) {
      console.warn("Failed to persist week bar default visibility:", e);
    }
  }, []);

  // Update the existing isMasterMode effect
  useEffect(() => {
    if (!isMasterMode) return;
    let alive = true;
    (async () => {
      try {
        const [ms, ma] = await Promise.all([
           fetchMasterScheduleSessions(),
           fetchAllMasterAvailability() // Fetch all availabilities
        ]);
        
        const days = DAYS_ORDER;
        const cfgs = {};
        await Promise.all(days.map(async d => {
           const c = await fetchMasterDayConfig(d);
           if(c) cfgs[d] = c;
        }));

        if (alive) {
          setMasterSessions(ms);
          setMasterAvailability(ma);
          setMasterDayConfigs(cfgs);
        }
      } catch (err) {
        console.error("Failed to load master data", err);
      }
    })();
    return () => { alive = false; };
  }, [isMasterMode, masterRefreshKey]);

  const handleSaveUiSettings = useCallback(async ({ newTutorOrder }) => {
    // 1. Save Tutor Order (This is the part that was missing!)
    if (newTutorOrder && Array.isArray(newTutorOrder)) {
      setTutorOrder(newTutorOrder); // Update local list immediately
      try {
        await saveTutorOrder(newTutorOrder); // Save to Database
      } catch (e) {
        console.error("Failed to save tutor order", e);
      }
    }

    setIsTutorOrderOpen(false);
  }, []);

  const isAnyModalOpen =
    !!createCtx ||
    !!editSession ||
    !!dayEdit ||
    !!dupModal ||
    !!masterDayEditKey ||
    !!createEventCtx ||
    !!editEvent ||
    !!isTrashModalOpen ||
    !!isSettingsOpen ||
    !!isScheduleCalendarOpen ||
    !!isTutorOrderOpen;

  useEffect(() => {
    let alive = true;
    (async () => {
      // Fetch Tutors, Students, AND Order
      const [t, s, order] = await Promise.all([
        fetchActiveTutors(),
        fetchAllStudents(),
        fetchTutorOrder()
      ]);
      if (!alive) return;
      setTutors(t);
      setStudents(s);
      setTutorOrder(order);
    })();
    return () => { alive = false; };
  }, []);

  // Hydrate the editable default weekly schedule so unscheduled dates resolve
  // against the admin-configured default (falls back to the built-in default).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cfg = await fetchDefaultScheduleWeekConfig();
        if (!alive) return;
        setActiveDefaultWeekConfig(cfg);
        setRefreshKey((x) => x + 1);
      } catch (e) {
        console.warn("Failed to hydrate default schedule.", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Availability + clocks — regular fetch, re-runs when week/tutor changes or after manual actions
  useEffect(() => {
    let alive = true;
    (async () => {
      const rangeStart = addDays(weekStart, -2);
      const rangeEnd = addDays(weekEnd, 3);

      const [avail, clocks] = await Promise.all([
        fetchAvailabilityBlocks({ rangeStart, rangeEnd, tutorUid: selectedTutorUid }),
        fetchTutorClockIntervals({ rangeStart, rangeEnd, tutorUid: selectedTutorUid }),
      ]);

      if (!alive) return;
      setAvailability(avail);
      setClockIntervals(clocks);
    })();
    return () => { alive = false; };
  }, [weekStart, weekEnd, selectedTutorUid, refreshKey]);

  // Sessions — real-time via Firestore onSnapshot (no manual refresh needed)
  useEffect(() => {
    const rangeStart = addDays(weekStart, -2);
    const rangeEnd = addDays(weekEnd, 3);
    const startTs = Timestamp.fromDate(rangeStart);
    const endTs = Timestamp.fromDate(rangeEnd);

    let q = query(
      collection(db, SESSIONS_COL),
      where("start", ">=", startTs),
      where("start", "<", endTs)
    );
    if (selectedTutorUid) {
      q = query(q, where("tutorUid", "==", selectedTutorUid));
    }

    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs
        .map((d) => {
          const data = d.data();
          const toDate = (ts) => {
            if (!ts) return null;
            if (ts instanceof Date) return ts;
            if (typeof ts.toDate === "function") return ts.toDate();
            return null;
          };
          return { id: d.id, ...data, start: toDate(data.start), end: toDate(data.end) };
        })
        .filter((s) => !s?.isDeleted);
      setSessions(rows);
    }, (err) => {
      console.error("Sessions real-time listener error:", err);
    });

    return () => unsub();
  }, [weekStart, weekEnd, selectedTutorUid]);

  // Events real-time listener — covers the current week OR a wide month range for month view.
  // Month view subscribes to ±4 months from monthCursor so continuous scroll has events loaded.
  useEffect(() => {
    if (isMasterMode) {
      setEvents([]);
      return undefined;
    }
    const isMonthView = viewMode === "month";
    const rangeStart = isMonthView
      ? addMonthsLocal(monthCursor, -4) // 4 months before for backward scroll
      : addDays(weekStart, -2);
    const rangeEnd = isMonthView
      ? addMonthsLocal(monthCursor, 5)  // 4 months after + current for forward scroll
      : addDays(weekEnd, 3);
    const unsub = subscribeToEventsForRange(
      rangeStart,
      rangeEnd,
      (evts) => setEvents(evts),
      (err) => console.error("Events real-time listener error:", err)
    );
    return () => unsub();
  }, [weekStart, weekEnd, viewMode, monthCursor, isMasterMode]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isMasterMode || !visibleStudents.length) {
        if (alive) setDiagnosticNotificationsByStudentId({});
        return;
      }

      try {
        const nextMap = await fetchDiagnosticNotificationsByStudentId(visibleStudents, {
          activeDays: SCHEDULE_NOTIFICATION_WINDOWS.diagnosticDays,
          now: new Date(),
        });
        if (alive) setDiagnosticNotificationsByStudentId(nextMap || {});
      } catch (error) {
        console.error("Failed to load schedule diagnostic notifications", error);
        if (alive) setDiagnosticNotificationsByStudentId({});
      }
    })();

    return () => {
      alive = false;
    };
  }, [visibleStudents, isMasterMode]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isMasterMode) {
        if (alive) setSessionLogBySessionId({});
        return;
      }

      const sessionIds = [...new Set((sessions || []).map((s) => s?.id).filter(Boolean))];
      if (!sessionIds.length) {
        if (alive) setSessionLogBySessionId({});
        return;
      }

      try {
        const logs = await fetchSessionLogsBySessionIds(sessionIds);
        if (alive) setSessionLogBySessionId(logs || {});
      } catch (error) {
        console.error("Failed to load session log indicators", error);
        if (alive) setSessionLogBySessionId({});
      }
    })();

    return () => {
      alive = false;
    };
  }, [sessions, isMasterMode]);

  // --- REMOVED THE OLD TRASH useEffect HERE ---

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i <= 6; i++) {
      const d = addDays(weekStart, i);
      const dayKey = getDayKeyForDate(d);
      if (dayKey) days.push({ dayKey, date: d });
    }
    return days.filter((x) => DAYS_ORDER.includes(x.dayKey));
  }, [weekStart]);

  const sortedTutors = useMemo(() => {
    if (!tutorOrder || tutorOrder.length === 0) return tutors;

    return [...tutors].sort((a, b) => {
      const idxA = tutorOrder.indexOf(a.id);
      const idxB = tutorOrder.indexOf(b.id);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return 0;
    });
  }, [tutors, tutorOrder]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const dates = [];
      for (let offset = -45; offset <= 75; offset += 1) {
        dates.push(addDays(weekStart, offset));
      }
      const cfgs = await fetchDayConfigsForDates(dates);
      if (!alive) return;
      setDayConfigs(cfgs);
      setHasHydratedDayConfigs(true);
    })();
    return () => {
      alive = false;
    };
  }, [weekStart, refreshKey]);

  const model = useMemo(() => {
    const result = {};

    for (const { dayKey, date } of weekDays) {
      const dk = dateKeyLocal(date);
      const cfg = dayConfigs[dk];
      const resolvedDay = resolveScheduleForDate(date, cfg);
      const timeBlocks = cfg?.timeBlocks?.length ? cfg.timeBlocks : defaultBlocksForDayKey(dayKey);
      const isClosed = !!resolvedDay.isClosed;
      const lockedPastDay = isPastDayLocked(date);
      const { startMin: timelineStartMin, endMin: timelineEndMin } = resolveTimelineRangeForDay(dayKey, {
        timelineStartMin: resolvedDay.startMin,
        timelineEndMin: resolvedDay.endMin,
      });

      const tutorList = selectedTutorUid ? sortedTutors.filter((t) => t.id === selectedTutorUid) : sortedTutors;

      const tutorRows = [];
      for (const tutor of tutorList) {
        const availabilityRecordsForTutorDay = availability.filter(
          (a) => a.uid === tutor.id && isSameDay(a.start, date)
        );
        const latestAvailRecord = pickLatestAvailabilityRecord(availabilityRecordsForTutorDay);
        const availabilityState = resolveAvailabilityForDay(latestAvailRecord, dayKey);
        const effectiveAvailability =
          availabilityState.unavailable
            ? []
            : [{ startMin: availabilityState.startMin, endMin: availabilityState.endMin }];
        const hasTimelineAvailability = effectiveAvailability.some(
          (seg) => seg.startMin < timelineEndMin && timelineStartMin < seg.endMin
        );

        const sessForTutorDay = sessions.filter((s) => {
          if (s.tutorUid !== tutor.id) return false;
          if (!isSameDay(s.start, date)) return false;
          return true;
        });

        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = addDays(dayStart, 1);

        const clockForTutorDay = shouldShowClockIndicatorsForTutor(tutor)
          ? (clockIntervals || [])
            .filter((interval) => {
              if (interval.uid !== tutor.id) return false;
              return interval.start < dayEnd && interval.end > dayStart;
            })
            .map((interval) => {
              const clippedStart = interval.start < dayStart ? dayStart : interval.start;
              const clippedEnd = interval.end > dayEnd ? dayEnd : interval.end;
              const startMin = minuteOfDay(clippedStart);
              const endMin = minuteOfDay(clippedEnd);
              if (endMin <= startMin) return null;

              return {
                ...interval,
                start: clippedStart,
                end: clippedEnd,
                startMin,
                endMin,
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.startMin - b.startMin)
          : [];

        const blocksMap = {};
        for (const block of timeBlocks) {
          const isAvail = effectiveAvailability.some(
            (seg) => seg.startMin < block.endMin && block.startMin < seg.endMin
          );

          const seatArr = [ [], [], [] ]; 

          const sessForCell = sessForTutorDay.filter((s) => (s.blockId || "") === block.id);

          for (const s of sessForCell) {
            const idx = s.slotIdx ?? 0;
            if (!seatArr[idx]) continue;

            const durationMin = Math.round((s.end - s.start) / 60000);
            const blockDurationMin = block.endMin - block.startMin;

            const sessionObj = {
              ...s,
              studentLabel:
                s.studentName && s.studentGrade != null
                  ? `${s.studentName} (${s.studentGrade})`
                  : "Student",
              durationMin,
              blockDurationMin,
            };
            seatArr[idx].push(sessionObj);
          }

          blocksMap[block.id] = { isAvailable: isAvail, sessionLists: seatArr };
        }

        const hasSessions = sessForTutorDay.length > 0;
        const hasValidSessions = sessForTutorDay.some((s) => !isCanceledStatus(s.status));
        const hasClockIntervals = clockForTutorDay.length > 0;

        if (lockedPastDay) {
          if (!hasValidSessions) continue;
          const isGhost = hasSessions && !hasTimelineAvailability;
          tutorRows.push({
            tutor,
            blocks: blocksMap,
            isGhost,
            availabilities: effectiveAvailability,
            clockIntervals: clockForTutorDay,
          });
          continue;
        }

        if (availabilityState.hidden && !hasSessions && !hasClockIntervals) {
          continue;
        }
        
        if (hasTimelineAvailability || hasSessions || hasClockIntervals) {
          // Keep "Unavailable. Move Sessions." only for real scheduling conflicts:
          // unavailable tutor + at least one scheduled session.
          // Clock-only rows should remain visible without ghost warning styling.
          const isGhost = hasSessions && !hasTimelineAvailability;

          tutorRows.push({ 
            tutor, 
            blocks: blocksMap, 
            isGhost,
            availabilities: effectiveAvailability,
            clockIntervals: clockForTutorDay,
          }); 
        }
      }

      result[dayKey] = {
        date,
        timeBlocks,
        tutorRows,
        isClosed,
        isPastLocked: lockedPastDay,
        timelineStartMin,
        timelineEndMin,
      };
    }

    return result;
  }, [weekDays, sortedTutors, availability, sessions, selectedTutorUid, dayConfigs, clockIntervals, isPastDayLocked]);

  const handleSecretClockEdit = useCallback(async ({ tutor, interval }) => {
    const authUser = getAuth().currentUser;
    const editorEmail = String(authUser?.email || "").toLowerCase();
    const isAllowedEditor = editorEmail && DEV_CLOCK_EDIT_EMAILS.includes(editorEmail);

    if (!isAllowedEditor) {
      alert("Clock edit is restricted to developer accounts.");
      return;
    }

    if (DEV_CLOCK_EDIT_SECRET) {
      const providedSecret = window.prompt("Developer key required:");
      if (providedSecret == null) return;
      if (providedSecret.trim() !== DEV_CLOCK_EDIT_SECRET) {
        alert("Invalid developer key.");
        return;
      }
    }

    const eventDocPath = interval?.isOpen
      ? `/active_sessions/${interval?.docId || interval?.uid || ""}`
      : `/clock_events/${interval?.docId || interval?.id || ""}`;
    if (eventDocPath && window.confirm(`Developer path:\n${eventDocPath}\n\nCopy this path and continue editing?`)) {
      navigator?.clipboard?.writeText?.(eventDocPath).catch(() => {});
    }

    const defaultIn = interval.start instanceof Date
      ? interval.start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : "";

    const rawClockIn = window.prompt(
      `Edit clock-in for ${tutor?.name || "Tutor"}\nAccepted: 5:35 PM or 17:35`,
      defaultIn
    );
    if (rawClockIn == null) return;

    const nextClockInAt = parseClockEditInput(interval.start || new Date(), rawClockIn);
    if (!(nextClockInAt instanceof Date)) {
      alert("Invalid clock-in format. Use 5:35 PM or 17:35.");
      return;
    }

    let nextClockOutAt = null;
    if (!interval.isOpen) {
      const defaultOut = interval.end instanceof Date
        ? interval.end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
        : "";
      const rawClockOut = window.prompt(
        `Edit clock-out for ${tutor?.name || "Tutor"}\nAccepted: 7:55 PM or 19:55`,
        defaultOut
      );
      if (rawClockOut == null) return;

      const parsedClockOut = parseClockEditInput(interval.end || interval.start || new Date(), rawClockOut);
      if (!(parsedClockOut instanceof Date)) {
        alert("Invalid clock-out format. Use 7:55 PM or 19:55.");
        return;
      }

      nextClockOutAt = parsedClockOut;
      if (nextClockOutAt <= nextClockInAt) {
        nextClockOutAt = new Date(nextClockOutAt.getTime() + 24 * 60 * 60 * 1000);
      }
    }

    try {
      await adjustClockInterval({
        interval,
        nextClockInAt,
        nextClockOutAt,
        editorUid: authUser?.uid || null,
        editorEmail: authUser?.email || null,
      });
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to adjust clock interval", error);
      alert(`Failed to adjust clock time: ${error?.message || error}\n\nDoc path: ${eventDocPath}`);
    }
  }, []);

  const fullWeekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i <= 6; i++) {
      const d = addDays(weekStart, i);
      const dayKey = getDayKeyForDate(d);
      if (dayKey) days.push({ dayKey, date: d });
    }
    return days.filter((x) => DAYS_ORDER.includes(x.dayKey));
  }, [weekStart]);

  // Sun–Sat calendar week for the WeekEventHeader (mirrors how the Month view shows a week row).
  // weekStart is always Monday; Sunday is weekStart - 1 day.
  const calendarWeekDays = useMemo(() => {
    const sunday = addDays(weekStart, -1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(sunday, i);
      return { date: d, dayKey: getDayKeyForDate(d) || "" };
    });
  }, [weekStart]);

  const visibleDays = useMemo(() => {
    if (viewMode === "week") return fullWeekDays;
    if (viewMode === "month" || viewMode === "year") return [];
    return fullWeekDays.filter((d) => d.dayKey === viewMode);
  }, [fullWeekDays, viewMode]);

  const currentDayDate = useMemo(() => {
    if (viewMode === "week" || viewMode === "month" || viewMode === "year") return null;
    return fullWeekDays.find((d) => d.dayKey === viewMode)?.date || null;
  }, [fullWeekDays, viewMode]);

  const currentScheduleSnapshotRef = useRef(null);
  currentScheduleSnapshotRef.current = {
    isMasterMode,
    masterTargetWeekStart,
    viewMode,
    weekStart,
    monthCursor,
    yearCursor,
    currentDayDate,
  };

  useLayoutEffect(() => {
    if (!scheduleRouteState?.matches) return;

    const routeKey = `${location.pathname}${location.search}`;
    const snapshot = currentScheduleSnapshotRef.current;
    let stateChanged = false;

    if (scheduleRouteState.isExplicit || scheduleRouteState.isMasterMode) {
      userChangedViewRef.current = true;
      initialViewAppliedRef.current = true;
    }

    if (scheduleRouteState.isMasterMode) {
      if (!snapshot?.isMasterMode) {
        setIsMasterMode(true);
        setMasterRefreshKey((prev) => prev + 1);
        stateChanged = true;
      }
      if (
        scheduleRouteState.weekStart &&
        !isSameRouteDate(snapshot?.masterTargetWeekStart, scheduleRouteState.weekStart)
      ) {
        setMasterTargetWeekStart(scheduleRouteState.weekStart);
        stateChanged = true;
      }
      if (snapshot?.viewMode !== "week") {
        setViewMode("week");
        stateChanged = true;
      }
      if (stateChanged) {
        routeApplyInFlightRef.current = routeKey;
      }
      return;
    }

    if (snapshot?.isMasterMode) {
      setIsMasterMode(false);
      stateChanged = true;
    }

    if (!scheduleRouteState.isExplicit) {
      if (stateChanged) {
        routeApplyInFlightRef.current = routeKey;
      }
      return;
    }

    if (scheduleRouteState.weekStart && !isSameRouteDate(snapshot?.weekStart, scheduleRouteState.weekStart)) {
      setWeekStart(scheduleRouteState.weekStart);
      stateChanged = true;
    }

    if (scheduleRouteState.dayDate) {
      const nextMonthCursor = startOfMonthLocal(scheduleRouteState.dayDate);
      const nextYearCursor = startOfYearLocal(scheduleRouteState.dayDate);
      if (!isSameRouteDate(snapshot?.monthCursor, nextMonthCursor)) {
        setMonthCursor(nextMonthCursor);
        stateChanged = true;
      }
      if (!isSameRouteDate(snapshot?.yearCursor, nextYearCursor)) {
        setYearCursor(nextYearCursor);
        stateChanged = true;
      }
    }

    if (scheduleRouteState.monthCursor) {
      const nextMonthCursor = startOfMonthLocal(scheduleRouteState.monthCursor);
      const nextYearCursor = startOfYearLocal(scheduleRouteState.monthCursor);
      if (!isSameRouteDate(snapshot?.monthCursor, nextMonthCursor)) {
        setMonthCursor(nextMonthCursor);
        stateChanged = true;
      }
      if (!isSameRouteDate(snapshot?.yearCursor, nextYearCursor)) {
        setYearCursor(nextYearCursor);
        stateChanged = true;
      }
    }

    if (scheduleRouteState.yearCursor && !isSameRouteDate(snapshot?.yearCursor, scheduleRouteState.yearCursor)) {
      setYearCursor(scheduleRouteState.yearCursor);
      stateChanged = true;
    }

    if (scheduleRouteState.viewMode && snapshot?.viewMode !== scheduleRouteState.viewMode) {
      setViewMode(scheduleRouteState.viewMode);
      stateChanged = true;
    }

    if (stateChanged) {
      routeApplyInFlightRef.current = routeKey;
    }
  }, [location.pathname, location.search, scheduleRouteState]);

  useEffect(() => {
    if (!scheduleRouteState?.matches) return;
    if (!scheduleRouteState.isExplicit && !initialViewAppliedRef.current) return;

    const currentRoute = `${location.pathname}${location.search}`;
    const nextRoute = buildScheduleRoute({
      viewMode: isMasterMode ? "week" : viewMode,
      weekStart: isMasterMode ? masterTargetWeekStart : weekStart,
      currentDayDate,
      monthCursor,
      yearCursor,
      isMasterMode,
    });

    if (routeApplyInFlightRef.current) {
      if (routeApplyInFlightRef.current === currentRoute) {
        if (currentRoute === nextRoute) {
          routeApplyInFlightRef.current = null;
        }
        return;
      }
      routeApplyInFlightRef.current = null;
    }

    if (currentRoute === nextRoute) return;

    navigate(nextRoute, { replace: !scheduleRouteState.isExplicit });
  }, [
    currentDayDate,
    isMasterMode,
    location.pathname,
    location.search,
    masterTargetWeekStart,
    monthCursor,
    navigate,
    scheduleRouteState,
    viewMode,
    weekStart,
    yearCursor,
  ]);

  const drillToDate = useCallback(
    (date) => {
      if (!date) return;
      const selectedDate = clampDateToMidnight(date);
      setMonthSelectedDate(selectedDate);
      setMonthCursor(startOfMonthLocal(selectedDate));
      setYearCursor(startOfYearLocal(selectedDate));

      if (viewMode === "month") {
        setMonthScrollTrigger((x) => x + 1);
        return;
      }

      if (viewMode === "week") {
        const ws = startOfWeekMonday(selectedDate);
        setWeekStart(ws);
        setViewMode("week");
        return;
      }

      const ws = startOfWeekMonday(selectedDate);
      setWeekStart(ws);

      const dk = getDayKeyForDate(selectedDate);
      if (dk && DAYS_ORDER.includes(dk)) {
        setViewMode(dk);
        setTimeout(() => {
          scrollToDayKey(dk);
        }, 60);
      } else {
        setViewMode("week");
        setTimeout(() => {
          scrollToDayKey(DAYS_ORDER[0]);
        }, 60);
      }
    },
    [scrollToDayKey, viewMode]
  );

  const handlePrevDay = useCallback(() => {
    const anchor = currentDayDate || addDays(weekStart, 0);
    for (let offset = 1; offset <= 14; offset += 1) {
      const candidate = addDays(anchor, -offset);
      const dk = getDayKeyForDate(candidate);
      if (!dk || isDateClosed(candidate)) continue;
      setWeekStart(startOfWeekMonday(candidate));
      setViewMode(dk);
      return;
    }
  }, [currentDayDate, isDateClosed, weekStart]);

  const handleNextDay = useCallback(() => {
    const anchor = currentDayDate || addDays(weekStart, 0);
    for (let offset = 1; offset <= 14; offset += 1) {
      const candidate = addDays(anchor, offset);
      const dk = getDayKeyForDate(candidate);
      if (!dk || isDateClosed(candidate)) continue;
      setWeekStart(startOfWeekMonday(candidate));
      setViewMode(dk);
      return;
    }
  }, [currentDayDate, isDateClosed, weekStart]);

  const handleKeyboardPrev = useCallback(() => {
    if (viewMode === "week") {
      if (isMasterMode) setMasterTargetWeekStart(addDays(masterTargetWeekStart, -7));
      else setWeekStart(addDays(weekStart, -7));
      return;
    }

    if (DAYS_ORDER.includes(viewMode)) {
      handlePrevDay();
    }
  }, [viewMode, isMasterMode, masterTargetWeekStart, weekStart, handlePrevDay]);

  const handleKeyboardNext = useCallback(() => {
    if (viewMode === "week") {
      if (isMasterMode) setMasterTargetWeekStart(addDays(masterTargetWeekStart, 7));
      else setWeekStart(addDays(weekStart, 7));
      return;
    }

    if (DAYS_ORDER.includes(viewMode)) {
      handleNextDay();
    }
  }, [viewMode, isMasterMode, masterTargetWeekStart, weekStart, handleNextDay]);

  function handleToday() {
    const todayNow = clampDateToMidnight(new Date());

    // ── Month view: stay in Month view, scroll to today's month ──
    if (viewMode === "month") {
      setMonthSelectedDate(todayNow);
      setMonthCursor(startOfMonthLocal(todayNow));
      // Increment scrollTrigger so ScheduleMonthView always scrolls even if
      // monthCursor value didn't change (user was already on today's month but
      // had scrolled elsewhere in the continuous calendar).
      setMonthScrollTrigger((x) => x + 1);
      return;
    }

    const target = resolveTodayOrNextOpenDate(todayNow);

    // Reset month/year cursors so mini calendar in WeekBar shows correct month
    setMonthCursor(startOfMonthLocal(todayNow));
    setYearCursor(startOfYearLocal(todayNow));

    // ── Day view: navigate to smart target day ──
    const isDayView = DAYS_ORDER.includes(viewMode);
    if (isDayView) {
      setWeekStart(startOfWeekMonday(target.date));
      setViewMode(target.dayKey);
      setTimeout(() => scrollToDayKey(target.dayKey), 50);
      return;
    }

    // ── Week view: reset to the week containing the smart target date ──
    // Use target.date (not todayNow) so that if today is e.g. Sunday,
    // we jump to the week containing the next open schedule day (Monday).
    setWeekStart(startOfWeekMonday(target.date));

    if (!DAYS_ORDER.includes(viewMode) && viewMode !== "week") {
      setViewMode("week");
    }

    setTimeout(() => scrollToDayKey(target.dayKey), 50);
  }

  const handlePrevMonth = useCallback(() => {
    setMonthCursor((prev) => addMonthsLocal(prev, -1));
    setViewMode("month");
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonthCursor((prev) => addMonthsLocal(prev, 1));
    setViewMode("month");
  }, []);

  const handlePrevYear = useCallback(() => {
    setYearCursor((prev) => startOfYearLocal(new Date(prev.getFullYear() - 1, 0, 1)));
    setViewMode("year");
  }, []);

  const handleNextYear = useCallback(() => {
    setYearCursor((prev) => startOfYearLocal(new Date(prev.getFullYear() + 1, 0, 1)));
    setViewMode("year");
  }, []);

  const clearSelection = useCallback(() => {
    selectedSessionIdRef.current = null;
    selectedSlotRef.current = null;

    setSelectedSessionId(null);
    setSelectedSlot(null);
  }, []);

  const closeCreateModal = useCallback(() => {
    setCreateCtx(null);
    selectedSlotRef.current = null;
    setSelectedSlot(null);
  }, []);

  const copySelected = useCallback(() => {
    if (!selectedSessionId) return;
    const sourceList = isMasterMode ? masterSessions : sessions;
    const s = sourceList.find((x) => x.id === selectedSessionId);
    if (!s) return;

    const durationMin = Math.max(5, Math.round((new Date(s.end) - new Date(s.start)) / 60000));

    const data = {
      type: "scheduled_session_clip",
      copiedAt: Date.now(),
      payload: {
        studentId: s.studentId,
        studentName: s.studentName,
        studentGrade: resolveCopiedStudentGrade(students, s),
        subject: s.subject || "",
        note: s.note || "",
        durationMin,
      },
    };

    setClipboard(data);
    saveClipboard(data);
  }, [selectedSessionId, sessions, masterSessions, isMasterMode, students]);

  const pasteToSelectedSlot = useCallback(async () => {
    const slot = selectedSlotRef.current;   
    if (!clipboard?.payload) return;
    if (!slot) return;
    if (!ensureDateIsEditable(slot.date)) return;
    
    const oldDuration = Math.max(5, Number(clipboard.payload.durationMin) || 0);

    const targetStartMin = slot.startMin ?? slot.subStartMin ?? slot.block.startMin;
    const targetEndMin = slot.maxEndMin ?? slot.subEndMin ?? slot.block.endMin;

    const start = minutesToDate(slot.date, targetStartMin);
    const calcEndMin = targetStartMin + oldDuration;
    if (calcEndMin > targetEndMin) {
      return alert("This start time does not fit the full session duration within tutor availability.");
    }

    const end = minutesToDate(slot.date, calcEndMin);

    const sourceList = isMasterMode ? masterSessions : sessions;
    const slotIdx = resolveLaneForRange(sourceList, {
      tutorUid: slot.tutorUid,
      date: slot.date,
      start,
      end,
      preferredLane: Number.isInteger(slot.slotIdx) ? slot.slotIdx : null,
    });
    if (slotIdx == null) return alert("This tutor already has 3 concurrent students for that time range.");

    const payload = {
      tutorUid: slot.tutorUid,
      tutorName: slot.tutorName,
      studentId: clipboard.payload.studentId,
      studentName: clipboard.payload.studentName,
      studentGrade: clipboard.payload.studentGrade,
      subject: clipboard.payload.subject,
      note: clipboard.payload.note,
      status: "Pending",
      start,
      end,
      dayKey: slot.dayKey,
      blockId: slot.block.id,
      slotIdx,
    };

    if (isMasterMode) {
      await createMasterScheduleSession(payload);
      setMasterRefreshKey((x) => x + 1);
    } else {
      await createScheduledSession(payload);
      setRefreshKey((x) => x + 1);
    }
  }, [clipboard, sessions, masterSessions, isMasterMode, ensureDateIsEditable]);

  const deleteSelected = useCallback(async () => {
    if (!selectedSessionId) return;

    if (isMasterMode) {
      if (!window.confirm("Delete this session?")) return;
      await deleteMasterScheduleSession(selectedSessionId);
      setSelectedSessionId(null);
      setMasterRefreshKey((x) => x + 1);
    } else {
      const current = sessions.find((s) => s.id === selectedSessionId);
      if (!ensureDateIsEditable(current?.start)) return;
      if (!window.confirm("Delete this session?")) return;
      
      // 1. Tell database to delete IMMEDIATELY in the background
      const deletePromise = deleteScheduledSession(selectedSessionId);

      // 2. Play the animation for the user
      if (current) {
        setSessionAnimation(selectedSessionId, "exit-delete", SESSION_ANIMATION_TIMINGS.deleteFadeOutMs + 1000);
        await waitForMs(SESSION_ANIMATION_TIMINGS.deleteFadeOutMs);
      }
      
      // 3. Make sure database is done, then refresh the screen
      await deletePromise;
      setSelectedSessionId(null);
      setRefreshKey((x) => x + 1);
    }
  }, [selectedSessionId, isMasterMode, sessions, setSessionAnimation, ensureDateIsEditable]);

  const moveSessionToSlot = useCallback(async (sessionId, targetSlot) => {
    const sourceList = isMasterMode ? masterSessions : sessions;
    const s = sourceList.find((x) => x.id === sessionId);
    if (!s) return;
    if (!isMasterMode && (!ensureDateIsEditable(s.start) || !ensureDateIsEditable(targetSlot?.date))) return;

    const oldDuration = Math.max(5, Math.round((new Date(s.end) - new Date(s.start)) / 60000));

    const targetStartMin = targetSlot.startMin ?? targetSlot.subStartMin ?? targetSlot.block.startMin;
    const targetEndMin = targetSlot.maxEndMin ?? targetSlot.subEndMin ?? targetSlot.block.endMin;
    
    const start = minutesToDate(targetSlot.date, targetStartMin);
    const calcEndMin = targetStartMin + oldDuration;
    if (calcEndMin > targetEndMin) {
      return alert("This start time does not fit the full session duration within tutor availability.");
    }

    const end = minutesToDate(targetSlot.date, calcEndMin);

    const slotIdx = resolveLaneForRange(sourceList, {
      excludeSessionId: sessionId,
      tutorUid: targetSlot.tutorUid,
      date: targetSlot.date,
      start,
      end,
      preferredLane: Number.isInteger(targetSlot.slotIdx)
        ? targetSlot.slotIdx
        : (Number.isInteger(s.slotIdx) ? s.slotIdx : null),
    });
    if (slotIdx == null) return alert("This tutor already has 3 concurrent students for that time range.");

    const patch = {
      tutorUid: targetSlot.tutorUid,
      tutorName: targetSlot.tutorName,
      dayKey: targetSlot.dayKey,
      blockId: targetSlot.block.id,
      slotIdx,
      start,
      end,
    };

    if (isMasterMode) {
      await updateMasterScheduleSession(sessionId, patch);
      setMasterRefreshKey((x) => x + 1);
    } else {
      await updateScheduledSession(sessionId, patch);
      setRefreshKey((x) => x + 1);
    }

    setSelectedSessionId(sessionId);
    setSelectedSlot({ ...targetSlot, slotIdx });
  }, [sessions, masterSessions, isMasterMode, ensureDateIsEditable]);

  const resizeSessionToRange = useCallback(async (sessionId, nextRange) => {
    if (!sessionId || !nextRange) return false;
    if (!Number.isFinite(nextRange.startMin) || !Number.isFinite(nextRange.endMin)) return false;
    if (nextRange.endMin <= nextRange.startMin) return false;

    const sourceList = isMasterMode ? masterSessions : sessions;
    const session = sourceList.find((x) => x.id === sessionId);
    if (!session) return false;

    const targetDate = nextRange.date ? new Date(nextRange.date) : new Date(session.start);
    if (!isMasterMode && !ensureDateIsEditable(targetDate)) return false;
    const tutorUid = nextRange.tutorUid || session.tutorUid;
    const tutorName = nextRange.tutorName || session.tutorName || "";
    const dayKey = nextRange.dayKey || session.dayKey;

    const start = minutesToDate(targetDate, nextRange.startMin);
    const end = minutesToDate(targetDate, nextRange.endMin);

    const lane = resolveLaneForRange(sourceList, {
      excludeSessionId: sessionId,
      tutorUid,
      date: targetDate,
      start,
      end,
      preferredLane: Number.isInteger(session.slotIdx) ? session.slotIdx : null,
    });

    if (lane == null) return false;

    const patch = {
      tutorUid,
      tutorName,
      dayKey,
      blockId: nextRange.blockId ?? session.blockId ?? "",
      slotIdx: lane,
      start,
      end,
    };

    if (isMasterMode) {
      await updateMasterScheduleSession(sessionId, patch);
      setMasterRefreshKey((x) => x + 1);
    } else {
      await updateScheduledSession(sessionId, patch);
      setRefreshKey((x) => x + 1);
    }

    selectedSessionIdRef.current = sessionId;
    selectedSlotRef.current = null;
    setSelectedSessionId(sessionId);
    setSelectedSlot(null);
    return true;
  }, [isMasterMode, masterSessions, sessions, ensureDateIsEditable]);

  const reorderSessionVisualOrder = useCallback(async (draggedSessionId, targetSessionId, ctx = {}) => {
    if (!draggedSessionId || !targetSessionId || draggedSessionId === targetSessionId) return false;

    const sourceList = isMasterMode ? masterSessions : sessions;
    const dragged = sourceList.find((s) => s.id === draggedSessionId);
    const target = sourceList.find((s) => s.id === targetSessionId);
    if (!dragged || !target) return false;
    if (isCanceledStatus(dragged.status) || isCanceledStatus(target.status)) return false;

    const dragDate = new Date(dragged.start);
    const targetDate = new Date(target.start);
    if (!isMasterMode && !ensureDateIsEditable(dragDate)) return false;
    if (!isSameDay(dragDate, targetDate)) return false;

    const tutorUid = ctx.tutorUid || dragged.tutorUid;
    if (!tutorUid || target.tutorUid !== tutorUid || dragged.tutorUid !== tutorUid) return false;

    if (!overlaps(new Date(dragged.start), new Date(dragged.end), new Date(target.start), new Date(target.end))) {
      return false;
    }

    const overlapStart = new Date(Math.max(new Date(dragged.start).getTime(), new Date(target.start).getTime()));
    const overlapEnd = new Date(Math.min(new Date(dragged.end).getTime(), new Date(target.end).getTime()));

    const group = sourceList
      .filter((s) => {
        if (s.tutorUid !== tutorUid) return false;
        if (!isSameDay(new Date(s.start), dragDate)) return false;
        if (isCanceledStatus(s.status)) return false;
        return overlaps(new Date(s.start), new Date(s.end), overlapStart, overlapEnd);
      })
      .sort((a, b) => {
        const orderDiff = visualOrderOfSession(a) - visualOrderOfSession(b);
        if (orderDiff !== 0) return orderDiff;
        const startDiff = new Date(a.start) - new Date(b.start);
        if (startDiff !== 0) return startDiff;
        return new Date(a.end) - new Date(b.end);
      });

    if (group.length < 2) return false;

    const withoutDragged = group.filter((s) => s.id !== draggedSessionId);
    const targetIndex = withoutDragged.findIndex((s) => s.id === targetSessionId);
    if (targetIndex < 0) return false;
    withoutDragged.splice(targetIndex, 0, dragged);

    const changed = withoutDragged.some((s, idx) => visualOrderOfSession(s) !== idx);
    if (!changed) return true;

    const patches = withoutDragged.map((s, idx) => ({
      id: s.id,
      patch: {
        visualOrder: idx,
      },
    }));

    if (isMasterMode) {
      await Promise.all(patches.map(({ id, patch }) => updateMasterScheduleSession(id, patch)));
      setMasterRefreshKey((x) => x + 1);
    } else {
      await Promise.all(patches.map(({ id, patch }) => updateScheduledSession(id, patch)));
      setRefreshKey((x) => x + 1);
    }

    return true;
  }, [isMasterMode, masterSessions, sessions, ensureDateIsEditable]);

  const handleSessionStatusChange = useCallback(
    async (sessionId, nextStatus) => {
      const sourceList = isMasterMode ? masterSessions : sessions;
      const session = sourceList.find((x) => x.id === sessionId);
      if (!session) return false;
      if (!isMasterMode && !ensureDateIsEditable(session.start)) return false;

      const currentIsCanceled = isCanceledStatus(session.status);
      const nextIsCanceled = isCanceledStatus(nextStatus);

      if (currentIsCanceled && !nextIsCanceled) {
        const sessionStart = new Date(session.start);
        const sessionEnd = new Date(session.end);

        const activeOverlaps = sourceList.filter((x) => {
          if (x.id === sessionId) return false;
          if (x.tutorUid !== session.tutorUid) return false;
          if (!isSameDay(new Date(x.start), sessionStart)) return false;
          if (isCanceledStatus(x.status)) return false;
          return overlaps(new Date(x.start), new Date(x.end), sessionStart, sessionEnd);
        });

        if (activeOverlaps.length >= 3) {
          alert("It is already full.");
          return false;
        }
      }

      if (isMasterMode) {
        await updateMasterScheduleSession(sessionId, { status: nextStatus });
        setMasterRefreshKey((x) => x + 1);
      } else {
        await updateSessionStatus(sessionId, nextStatus);
        setRefreshKey((x) => x + 1);
      }
      return true;
    },
    [isMasterMode, masterSessions, sessions, ensureDateIsEditable]
  );

  const dayEditInitialConfig = useMemo(() => {
    if (!dayEdit?.date || !dayEdit?.dayKey) return null;

    const k = dateKeyLocal(dayEdit.date);
    const existing = dayConfigs[k] || {};
    const defaults = defaultTimelineRangeForDayKey(dayEdit.dayKey);

    return {
      isClosed: !!existing.isClosed,
      timelineStartMin: Number.isFinite(Number(existing.timelineStartMin))
        ? Number(existing.timelineStartMin)
        : defaults.startMin,
      timelineEndMin: Number.isFinite(Number(existing.timelineEndMin))
        ? Number(existing.timelineEndMin)
        : defaults.endMin,
    };
  }, [dayEdit, dayConfigs]);

  const dayEditInitialAvailability = useMemo(() => {
    if (!dayEdit?.date || !dayEdit?.dayKey) return [];

    const dayAvailRecords = availability.filter((a) => isSameDay(a.start, dayEdit.date));
    const byUid = new Map();

    for (const rec of dayAvailRecords) {
      const uid = rec?.uid;
      if (!uid) continue;
      const current = byUid.get(uid);
      if (!current || timestampToMillis(rec?.updatedAt) > timestampToMillis(current?.updatedAt)) {
        byUid.set(uid, rec);
      }
    }

    return sortedTutors.map((tutor) => ({
      uid: tutor.id,
      ...resolveAvailabilityForDay(byUid.get(tutor.id), dayEdit.dayKey),
    }));
  }, [dayEdit, availability, sortedTutors]);

  useAdminScheduleHotkeys({
    enabled: !isAnyModalOpen,
    onCopy: copySelected,
    onPaste: pasteToSelectedSlot,
    onDelete: deleteSelected,
    onClearSelection: clearSelection,
    onArrowLeft: handleKeyboardPrev,
    onArrowRight: handleKeyboardNext,
  });

  const openDuplicateModal = useCallback(
    (mode) => {
      // Defaults are anchored to the real "today" week, not the currently
      // viewed week. The admin can freely pick any source/destination week
      // from inside the modal.
      const thisWeekStart = startOfWeekMonday(new Date());
      setDupModal({
        defaultMode: mode === "master" ? "master" : "week",
        defaultSourceWeekStart: thisWeekStart,
        defaultTargetWeekStart: addWeekOffset(thisWeekStart, 1),
      });
    },
    []
  );

  const handleDuplicateWeek = useCallback(() => {
    openDuplicateModal("week");
  }, [openDuplicateModal]);

  const handleBulkUpdateDayStatus = useCallback(
    async (dateObj, fromStatusLower, toStatusValue) => {
      if (!ensureDateIsEditable(dateObj)) return;
      const targets = sessions.filter((s) => {
        if (selectedTutorUid && s.tutorUid !== selectedTutorUid) return false;
        if (!isSameDay(s.start, dateObj)) return false;
        const st = (s.status || "Pending").toLowerCase();
        return st === fromStatusLower;
      });

      if (targets.length === 0) {
        alert(`No ${fromStatusLower} sessions found for this day.`);
        return;
      }

      const labelFrom = fromStatusLower.charAt(0).toUpperCase() + fromStatusLower.slice(1);
      if (!window.confirm(`Update ${targets.length} session(s) from ${labelFrom} to ${toStatusValue}?`)) return;

      try {
        await batchUpdateSessions(
          targets.map((s) => ({ id: s.id, patch: { status: toStatusValue } }))
        );
        setRefreshKey((x) => x + 1);
      } catch (err) {
        console.error(err);
        alert("Error updating session statuses. Check console.");
      }
    },
    [sessions, selectedTutorUid, ensureDateIsEditable]
  );

  const handleBulkDeleteDaySessions = useCallback(
    async (dateObj) => {
      if (!ensureDateIsEditable(dateObj)) return;
      const targets = sessions.filter((s) => {
        if (selectedTutorUid && s.tutorUid !== selectedTutorUid) return false;
        return isSameDay(s.start, dateObj);
      });

      if (targets.length === 0) {
        alert("No sessions found for this day.");
        return;
      }

      const scopeText = selectedTutorUid ? "for the selected tutor" : "for ALL tutors";
      if (
        !window.confirm(
          `Delete ${targets.length} session(s) on this day ${scopeText}?\n\nYou can restore deleted sessions from the Trash panel.`
        )
      )
        return;

      try {
        await batchDeleteSessionsByIds(targets.map((s) => s.id));
        setRefreshKey((x) => x + 1);
      } catch (err) {
        console.error(err);
        alert("Error deleting sessions. Check console.");
      }
    },
    [sessions, selectedTutorUid, ensureDateIsEditable]
  );

  const handleToggleMasterMode = useCallback(() => {
      setIsMasterMode((prev) => {
        const nextMode = !prev;
        if (nextMode) {
          setViewMode("week");
          setMasterTargetWeekStart(weekStart);
        }
        return nextMode;
      });

      if (!isMasterMode) {
        setMasterRefreshKey((prev) => prev + 1);
      }
    }, [isMasterMode, weekStart]); 

  const handleDuplicateMasterToWeek = useCallback(() => {
    openDuplicateModal("master");
  }, [openDuplicateModal]);

  const handleOpenSessionLogReview = useCallback((sessionToReview) => {
    if (!sessionToReview?.id) return;
    localStorage.setItem(`sessionLog_${sessionToReview.id}`, JSON.stringify(sessionToReview));
    localStorage.setItem(`tutorSession_${sessionToReview.id}`, JSON.stringify(sessionToReview));
    // adminEntry=1 tells the form that an admin is authoring this log on the
    // tutor's behalf, so the resulting log records who actually typed it in.
    window.open(`/tutor-session-form?id=${sessionToReview.id}&adminEntry=1`, "_blank");
  }, []);

  const navigateToPlacementForAnimation = useCallback(
    async (sessionLike) => {
      const target = resolvePlacementTarget(sessionLike);
      if (!target) return;

      if (weekStart.getTime() !== target.weekStart.getTime()) {
        setWeekStart(target.weekStart);
      }

      if (viewMode === "month" || viewMode === "year") {
        setViewMode("week");
      } else if (DAYS_ORDER.includes(viewMode)) {
        setViewMode(target.dayKey);
      }

      await waitForMs(SESSION_ANIMATION_TIMINGS.navigationSettleMs);
      scrollToDayKey(target.dayKey, 12);
    },
    [scrollToDayKey, viewMode, weekStart]
  );

  const animateLiveSessionCreate = useCallback(
    async (payload) => {
      if (!ensureDateIsEditable(payload?.start)) return null;
      // 1. Start creating and navigating at the exact same time
      const createPromise = createScheduledSession(payload);
      const navPromise = navigateToPlacementForAnimation(payload);
      
      // 2. Wait for both to finish
      const createdId = await createPromise;
      await navPromise;

      // 3. Refresh and trigger fade-in
      setRefreshKey((x) => x + 1);
      if (createdId) {
        setSessionAnimation(createdId, "enter");
      }
      return createdId;
    },
    [navigateToPlacementForAnimation, setSessionAnimation, ensureDateIsEditable]
  );

  const animateLiveSessionPatch = useCallback(
    async (sessionId, patch) => {
      const current = sessions.find((s) => s.id === sessionId);
      if (!current) {
        if (!ensureDateIsEditable(patch?.start)) return;
        await updateScheduledSession(sessionId, patch);
        setRefreshKey((x) => x + 1);
        return;
      }
      const patchTargetDate = patch?.start ? new Date(patch.start) : current.start;
      if (!ensureDateIsEditable(patchTargetDate)) return;

      const placementChange = isPlacementPatch(patch);
      const patched = applyPatchPreview(current, patch);

      try {
        // 1. Tell database to update IMMEDIATELY in the background
        const updatePromise = updateScheduledSession(sessionId, patch);

        // 2. Play the fade-out animation if it's moving
        if (placementChange) {
          setSessionAnimation(sessionId, "exit", SESSION_ANIMATION_TIMINGS.moveFadeOutMs + 1000);
          await waitForMs(SESSION_ANIMATION_TIMINGS.moveFadeOutMs);
        }

        // 3. Make sure database is done before navigating/refreshing
        await updatePromise;

        if (placementChange) {
          await navigateToPlacementForAnimation(patched);
        }

        setRefreshKey((x) => x + 1);
        setSessionAnimation(sessionId, placementChange ? "enter" : "highlight");
      } catch (error) {
        clearSessionAnimation(sessionId);
        throw error;
      }
    },
    [clearSessionAnimation, navigateToPlacementForAnimation, sessions, setSessionAnimation, ensureDateIsEditable]
  );

  const animateLiveSessionDelete = useCallback(
    async (sessionId) => {
      const current = sessions.find((s) => s.id === sessionId);
      if (!ensureDateIsEditable(current?.start)) return;
      
      // 1. Tell database to delete IMMEDIATELY in the background
      const deletePromise = deleteScheduledSession(sessionId);

      // 2. Play the animation for the user
      if (current) {
        setSessionAnimation(sessionId, "exit-delete", SESSION_ANIMATION_TIMINGS.deleteFadeOutMs + 1000);
        await waitForMs(SESSION_ANIMATION_TIMINGS.deleteFadeOutMs);
      }

      // 3. Make sure database is done, then refresh the screen
      await deletePromise;
      setRefreshKey((x) => x + 1);
    },
    [sessions, setSessionAnimation, ensureDateIsEditable]
  );

  // ── Event handlers ──────────────────────────────────────────────────────

  const handleEventCreate = useCallback(async (payload) => {
    await createEvent(payload);
  }, []);

  const handleEventSave = useCallback(async (eventId, patch) => {
    await updateEvent(eventId, patch);
  }, []);

  const handleEventDelete = useCallback(async (eventId) => {
    await deleteEvent(eventId);
  }, []);

  const handleEventDrop = useCallback(async (eventId, { startTime, endTime }) => {
    await updateEvent(eventId, { startTime, endTime });
  }, []);

  // ────────────────────────────────────────────────────────────────────────

  const handleTrashRestoreSuccess = useCallback(
    async (restoredSession) => {
      setIsTrashModalOpen(false);

      if (isMasterMode) {
        setMasterRefreshKey((prev) => prev + 1);
        return;
      }

      setIsMasterMode(false);
      setViewMode("week");

      const target = resolvePlacementTarget(restoredSession || null);
      if (target?.weekStart) {
        setWeekStart(target.weekStart);
      }

      setRefreshKey((prev) => prev + 1);

      if (restoredSession?.id) {
        setSessionAnimation(restoredSession.id, "enter");
      }

      await waitForMs(SESSION_ANIMATION_TIMINGS.navigationSettleMs);
      const targetDay = target?.dayKey || DAYS_ORDER[0];
      scrollToDayKey(targetDay, 12);
    },
    [isMasterMode, scrollToDayKey, setSessionAnimation]
  );

  const handleConfirmDuplicate = useCallback(async ({ selectedDayKeys, mode, sourceWeekStart, targetWeekStart }) => {
    if (!dupModal) return;

    if (!targetWeekStart || (mode !== "master" && !sourceWeekStart)) {
      alert("Please choose both From and To weeks before duplicating.");
      return;
    }

    const sourceRangeStart = mode === "master" ? null : clampDateToMidnight(sourceWeekStart);
    const targetRangeStart = clampDateToMidnight(targetWeekStart);
    const targetRangeEndExclusive = addDays(targetRangeStart, 7);

    if (
      mode === "week" &&
      sourceRangeStart &&
      sourceRangeStart.getTime() === targetRangeStart.getTime()
    ) {
      alert("Source and target weeks cannot be the same. Please choose a different target week.");
      return;
    }

    const daySummary = (selectedDayKeys || [])
      .map((dayKey) => dayKey.charAt(0).toUpperCase() + dayKey.slice(1))
      .join(", ");

    const sourceLabel = mode === "master" ? "Master Schedule" : formatDateRangeLabel(sourceRangeStart);
    const targetLabel = formatDateRangeLabel(targetRangeStart);

    const firstConfirmMessage =
      `From: ${sourceLabel}\n` +
      `To: ${targetLabel}\n` +
      `Days: ${daySummary}\n\n` +
      `Are you sure you want to duplicate this schedule?`;

    if (!window.confirm(firstConfirmMessage)) {
      return;
    }

    try {
      const existingSessions = await fetchScheduledSessions({
        rangeStart: targetRangeStart,
        rangeEnd: targetRangeEndExclusive,
        includeDeleted: false,
      });

      const conflictSessions = existingSessions.filter((s) => {
        if (!selectedDayKeys || !selectedDayKeys.length) return true;
        return selectedDayKeys.includes(s.dayKey);
      });

      if (conflictSessions.length > 0) {
        const confirmMsg =
          `Are you sure you want to duplicate into this week?\n\n` +
          `There are already ${conflictSessions.length} session(s) in the target week on the selected day(s).\n` +
          `Duplicating will remove all existing sessions in that target range.\n\n` +
          `Continue and overwrite?`;

        if (!window.confirm(confirmMsg)) {
          return;
        }
      }

      let count = 0;
      if (mode === "master") {
        count = await duplicateMasterScheduleToWeek(targetRangeStart, selectedDayKeys);
      } else {
        count = await duplicateScheduleRangeToTargetWeek(
          sourceRangeStart,
          addDays(sourceRangeStart, 7),
          targetRangeStart,
          selectedDayKeys
        );
      }

      const selectedDaySet =
        Array.isArray(selectedDayKeys) && selectedDayKeys.length ? new Set(selectedDayKeys) : null;
      const targetSessions = await fetchScheduledSessions({
        rangeStart: targetRangeStart,
        rangeEnd: targetRangeEndExclusive,
        includeDeleted: false,
      });
      const animatedIds = targetSessions
        .filter((session) => {
          if (!selectedDaySet) return true;
          return !!session?.dayKey && selectedDaySet.has(session.dayKey);
        })
        .map((session) => session?.id)
        .filter(Boolean);

      setDupModal(null);
      setIsMasterMode(false);
      setViewMode("week");
      setWeekStart(targetRangeStart);
      setRefreshKey((prev) => prev + 1);

      if (count > 0) {
        const animationCap = 80;
        animatedIds.slice(0, animationCap).forEach((sessionId) => {
          setSessionAnimation(sessionId, "enter");
        });
      }

      await waitForMs(SESSION_ANIMATION_TIMINGS.navigationSettleMs);
      const primaryDay =
        DAYS_ORDER.find((dayKey) => selectedDaySet?.has(dayKey)) || DAYS_ORDER[0];
      scrollToDayKey(primaryDay, 12);

      alert(`Successfully duplicated ${count} sessions.`);

    } catch (err) {
      console.error(err);
      alert("Error duplicating sessions. Check console.");
    }
  }, [dupModal, setSessionAnimation, scrollToDayKey]);

  let sbTitle = undefined;
  let sbSubtitle = undefined;

  if (isMasterMode) {
    sbTitle = "Master Schedule";
    sbSubtitle = "";
  } else if (currentDayDate) {
    // UPDATED: Split into two lines
    // Line 1: Weekday (e.g., "Monday")
    sbTitle = currentDayDate.toLocaleDateString("en-US", { weekday: "long" });
    
    // Line 2: Date (e.g., "February 2, 2026")
    sbSubtitle = currentDayDate.toLocaleDateString("en-US", { 
      month: "long", 
      day: "numeric", 
      year: "numeric" 
    });
  }

  return (
    <div
      className="adminSchedWrap"
      ref={wrapRef}
      style={animationCssVars}
      onMouseDown={() => { if (!isAnyModalOpen) clearSelection(); }}
    >
      {/* Floating drawer toggle button */}
      {!isDrawerOpen && (
        <button
          type="button"
          className="schedDrawerToggleBtn"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="Open schedule sidebar"
          title="Open sidebar"
        >
          ☰
        </button>
      )}

      {/* 1. Modals that float above everything */}
      <TrashModal
        isOpen={isTrashModalOpen}
        onClose={() => setIsTrashModalOpen(false)}
        isMasterMode={isMasterMode}
        selectedTutorUid={selectedTutorUid}
        weekStart={isMasterMode ? masterTargetWeekStart : weekStart}
        weekEnd={isMasterMode ? addDays(masterTargetWeekStart, 6) : weekEnd}
        onRestoreSuccess={handleTrashRestoreSuccess}
      />

      {/* 2. Full-width schedule layout (WeekBar is now a fixed overlay drawer) */}
      <div className="adminSchedLayout">

        {/* Schedule area takes full width */}
        <div className="adminSchedMain adminSchedMain--full">
          <div className="schedZoomOuter" style={{ "--sched-zoom": (uiZoom * 0.8).toFixed(3) }}>
            <div className="schedZoomInner">
              
              {/* Logic to show Master Schedule OR Regular Schedule */}
              {isMasterMode ? (
                <MasterScheduleView
                  viewMode={viewMode}
                  sortedTutors={sortedTutors}
                  selectedTutorUid={selectedTutorUid}
                  masterSessions={masterSessions}
                  masterAvailability={masterAvailability}
                  masterDayConfigs={masterDayConfigs}
                  onEditDay={(dk) => setMasterDayEditKey(dk)}
                  tutors={tutors}
                  dayRefs={dayRefs}
                  timeSlotLayout={uiSettings.timeSlotLayout}
                  selectedSessionId={selectedSessionId}
                  selectedSlot={selectedSlot}
                  selectedSessionIdRef={selectedSessionIdRef}
                  selectedSlotRef={selectedSlotRef}
                  setSelectedSessionId={setSelectedSessionId}
                  setSelectedSlot={setSelectedSlot}
                  moveSessionToSlot={moveSessionToSlot}
                  onSessionResize={resizeSessionToRange}
                  onSessionReorder={reorderSessionVisualOrder}
                  setCreateCtx={setCreateCtx}
                  setEditSession={setEditSession}
                />
              ) : viewMode === "month" ? (
                /* ── Month View: Events-only monthly calendar ── */
                <ScheduleMonthView
                  monthCursor={monthCursor}
                  selectedDate={monthSelectedDate}
                  scrollTrigger={monthScrollTrigger}
                  allEvents={events}
                  dayConfigsMap={dayConfigs}
                  onEventClick={(evt) => setEditEvent(evt)}
                  onCreateEvent={(date) => setCreateEventCtx({ date, dayKey: getDayKeyForDate(date), startMin: 14 * 60, endMin: 15 * 60 })}
                  onDrillToDate={drillToDate}
                />
              ) : (
                (viewMode === "week" || DAYS_ORDER.includes(viewMode)) && <>
                  {/* ── Week Event Header — Sun–Sat event strip; day clicks scroll within week ── */}
                  {viewMode === "week" && !isMasterMode && (
                    <WeekEventHeader
                      weekDays={calendarWeekDays}
                      allEvents={events}
                      onEventClick={(evt) => setEditEvent(evt)}
                      onScrollToDay={scrollToWeekDay}
                    />
                  )}
                  {visibleDays.map(({ dayKey }) => {
                    const dayData = model[dayKey];
                    if (!dayData) return null;
                    const dayLocked = !!dayData.isPastLocked;
                    const daySessionsForStatus = sessions.filter((s) => {
                      if (selectedTutorUid && s.tutorUid !== selectedTutorUid) return false;
                      return isSameDay(s.start, dayData.date);
                    });
                    const confirmedCount = daySessionsForStatus.filter(
                      (s) => (s.status || "Pending").toLowerCase() === "confirmed"
                    ).length;

                    return (
                      <DaySection
                          timeSlotLayout={uiSettings.timeSlotLayout} 
                          key={dayKey}
                          dayKey={dayKey}
                          date={dayData.date}
                          timeBlocks={dayData.timeBlocks}
                          timelineStartMin={dayData.timelineStartMin}
                          timelineEndMin={dayData.timelineEndMin}
                          tutorRows={dayData.tutorRows}
                          dayRef={(el) => (dayRefs.current[dayKey] = el)}
                          isClosed={dayData.isClosed}
                          readOnly={dayLocked}
                          onEditDay={(dk, d) => setDayEdit({ dayKey: dk, date: d })}
                          onBulkConfirmDay={dayLocked ? null : (dk, d) => handleBulkUpdateDayStatus(d, "pending", "Confirmed")}
                          onBulkPresentDay={dayLocked ? null : (dk, d) => handleBulkUpdateDayStatus(d, "confirmed", "Present")}
                          onBulkDeleteDay={dayLocked ? null : (dk, d) => handleBulkDeleteDaySessions(d)}
                          confirmedCount={confirmedCount}
                          onEmptySlotDoubleClick={dayLocked ? null : (ctx) => {
                            const tutor = tutors.find((t) => t.id === ctx.tutorUid);
                            setCreateCtx({ ...ctx, tutorName: tutor?.name || tutor?.email || "Tutor", dayKey });
                          }}
                          onSessionDoubleClick={(s) => setEditSession(s)}
                          onChangeSessionStatus={handleSessionStatusChange}
                          selectedSessionId={selectedSessionId}
                          selectedSlotKey={selectedSlot?.slotKey || null}
                          onSessionClick={(s) => {
                            selectedSessionIdRef.current = s.id;
                            selectedSlotRef.current = null;
                            setSelectedSessionId(s.id);
                            setSelectedSlot(null);
                          }}
                          onEmptySlotClick={(slot) => {
                            selectedSessionIdRef.current = null;
                            selectedSlotRef.current = slot;
                            setSelectedSlot(slot);
                            setSelectedSessionId(null);
                          }}
                          onSessionDragStart={(e, s) => {
                            e.dataTransfer.setData("text/plain", s.id);
                            selectedSessionIdRef.current = s.id;
                            selectedSlotRef.current = null;
                            setSelectedSessionId(s.id);
                            setSelectedSlot(null);
                          }}
                          onSlotDrop={async (draggedSessionId, slot) => {
                            await moveSessionToSlot(draggedSessionId, slot);
                          }}
                          onSessionResize={resizeSessionToRange}
                          onSessionReorder={reorderSessionVisualOrder}
                          onSecretClockEdit={handleSecretClockEdit}
                          sessionNotificationsBySessionId={sessionNotificationsBySessionId}
                          sessionLogBySessionId={sessionLogBySessionId}
                          showSessionLogIndicator={true}
                          showMissingSessionLogIndicator={true}
                          showCancelledMarkers={!(dayLocked && !isDeveloperMode)}
                          onOpenSessionLog={handleOpenSessionLogReview}
                          sessionAnimationById={sessionAnimationById}
                          events={computeEventsForDate(events, dateKeyLocal(dayData.date))}
                          selectedEventId={selectedEventId}
                          onEventClick={(evt) => setSelectedEventId(evt.id)}
                          onEventDoubleClick={(evt) => setEditEvent(evt)}
                          onEventTrackDoubleClick={(ctx) => {
                            if (dayLocked) return;
                            setCreateEventCtx(ctx);
                          }}
                          onEventDrop={dayLocked ? null : handleEventDrop}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {/* WeekBar is now a fixed overlay drawer */}
        <WeekBar
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          onToggleOpen={() => setIsDrawerOpen((prev) => !prev)}
          disableEscapeToggle={isAnyModalOpen}

          weekStart={isMasterMode ? masterTargetWeekStart : weekStart}
          weekEnd={isMasterMode ? addDays(masterTargetWeekStart, 6) : weekEnd}
          tutors={tutors}
          selectedTutorUid={selectedTutorUid}
          onSelectTutor={setSelectedTutorUid}
          onPrevWeek={() => {
            if (isMasterMode) setMasterTargetWeekStart(addDays(masterTargetWeekStart, -7));
            else setWeekStart(addDays(weekStart, -7));
          }}
          onNextWeek={() => {
            if (isMasterMode) setMasterTargetWeekStart(addDays(masterTargetWeekStart, 7));
            else setWeekStart(addDays(weekStart, 7));
          }}
          onToday={isMasterMode ? () => setMasterTargetWeekStart(startOfWeekMonday(new Date())) : handleToday}
          onOpenSettings={() => setIsSettingsOpen(true)}
          showWeekBarByDefault={!!uiSettings?.showWeekBarByDefault}
          onToggleWeekBarDefaultVisibility={handleSetWeekBarDefaultVisibility}
          isMasterMode={isMasterMode}
          onToggleMaster={handleToggleMasterMode}
          titleOverride={sbTitle}
          subtitleOverride={sbSubtitle}
          onDuplicateWeek={isMasterMode ? handleDuplicateMasterToWeek : handleDuplicateWeek}
          onOpenTrash={() => setIsTrashModalOpen(true)}

          uiZoom={uiZoom}
          onUiZoomChange={setUiZoom}

          viewMode={viewMode}
          onSetViewMode={(vm) => { userChangedViewRef.current = true; setViewMode(vm); }}
          currentDayDate={currentDayDate}
          onPrevDay={handlePrevDay}
          onNextDay={handleNextDay}

          monthCursor={monthCursor}
          yearCursor={yearCursor}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onPrevYear={handlePrevYear}
          onNextYear={handleNextYear}

          onPickDate={drillToDate}
          isDateClosed={isDateClosed}
        />
      </div>

      {/* 3. Pop-up Modals (Hidden by default) */}

      {/* Events Modals */}
      <CreateEventModal
        isOpen={!!createEventCtx}
        onClose={() => setCreateEventCtx(null)}
        ctx={createEventCtx}
        onCreate={handleEventCreate}
      />
      <EditEventModal
        isOpen={!!editEvent}
        onClose={() => { setEditEvent(null); setSelectedEventId(null); }}
        event={editEvent}
        onSave={handleEventSave}
        onDelete={handleEventDelete}
      />

      <DuplicateMenuModal
        isOpen={!!dupModal}
        onClose={() => setDupModal(null)}
        defaultMode={dupModal?.defaultMode || "week"}
        defaultSourceWeekStart={dupModal?.defaultSourceWeekStart || null}
        defaultTargetWeekStart={dupModal?.defaultTargetWeekStart || null}
        onConfirm={handleConfirmDuplicate}
      />
      <MasterDaySettingsModal 
        isOpen={!!masterDayEditKey}
        onClose={() => setMasterDayEditKey(null)}
        dayKey={masterDayEditKey || DAYS_ORDER[0]}
        tutors={tutors}
        initialDayConfig={masterDayConfigs[masterDayEditKey]}
        initialAvailability={masterAvailability.filter(a => a.dayKey === masterDayEditKey)}
        onSave={async ({ dayConfig, availabilityUpdates }) => {
           await upsertMasterDayConfig(masterDayEditKey, dayConfig);
           const promises = availabilityUpdates.map(av => 
             upsertMasterAvailability(masterDayEditKey, av.uid, {
               startMin: av.startMin,
               endMin: av.endMin,
               unavailable: av.unavailable,
               hidden: av.hidden
             })
           );
           await Promise.all(promises);
           setMasterRefreshKey(prev => prev + 1);
        }}
      />
      <SchedulingSettingsHub
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onOpenScheduleCalendar={() => {
          setIsSettingsOpen(false);
          setIsScheduleCalendarOpen(true);
        }}
        onOpenTutorOrder={() => setIsTutorOrderOpen(true)}
      />
      <SchedulingSettingsModal
        open={isTutorOrderOpen}
        onClose={() => setIsTutorOrderOpen(false)}
        tutors={tutors}
        currentTutorOrder={tutorOrder}
        onSave={handleSaveUiSettings}
      />
      <ScheduleCalendarView
        open={isScheduleCalendarOpen}
        onClose={() => setIsScheduleCalendarOpen(false)}
        onChanged={() => setRefreshKey((x) => x + 1)}
      />
      <CreateSessionModal
        isOpen={!!createCtx}
        onClose={closeCreateModal}
        ctx={createCtx}
        students={students}
        tutors={tutors}
        isMasterMode={isMasterMode}
        loadStudentSessionsForRange={async (studentId, rangeStart, rangeEnd) => {
          if (isMasterMode) return [];
          return fetchSessionsForStudentRange(studentId, rangeStart, rangeEnd);
        }}
        onCreate={async (payload) => {
          if (!isMasterMode && !ensureDateIsEditable(payload?.start)) return;
          closeCreateModal();
          if (isMasterMode) {
            await createMasterScheduleSession(payload);
            setMasterRefreshKey((x) => x + 1);
          } else {
            await animateLiveSessionCreate(payload);
          }
        }}
      />
      <EditSessionModal
        isOpen={!!editSession}
        onClose={() => setEditSession(null)}
        session={editSession}
        tutors={tutors}
        students={students}
        isMasterMode={isMasterMode}
        masterAvailability={masterAvailability}
        masterDayConfigs={masterDayConfigs}
        availabilityBlocks={isMasterMode ? [] : availability}
        allWeekSessions={isMasterMode ? masterSessions : sessions}
        onApplyPatch={async (id, patch) => {
          if (isMasterMode) {
            await updateMasterScheduleSession(id, patch);
            setMasterRefreshKey((x) => x + 1);
          } else {
            const sourceSession = sessions.find((s) => s.id === id);
            const patchTargetDate = patch?.start ? new Date(patch.start) : sourceSession?.start;
            if (!ensureDateIsEditable(patchTargetDate)) return;
            await animateLiveSessionPatch(id, patch);
          }
        }}
        onDelete={async (id) => {
          if (isMasterMode) {
            await deleteMasterScheduleSession(id);
            setMasterRefreshKey((x) => x + 1);
          } else {
            const sourceSession = sessions.find((s) => s.id === id);
            if (!ensureDateIsEditable(sourceSession?.start)) return;
            await animateLiveSessionDelete(id);
          }
        }}
        onCreateSession={async (payload) => {
          if (isMasterMode) return;
          if (!ensureDateIsEditable(payload?.start)) return;
          await animateLiveSessionCreate(payload);
        }}
        loadStudentSessionsForRange={async (studentId, rangeStart, rangeEnd) => {
          if (isMasterMode) return [];
          return fetchSessionsForStudentRange(studentId, rangeStart, rangeEnd);
        }}
        isDateEditable={ensureDateIsEditable}
        onOpenSessionLog={handleOpenSessionLogReview}
        sessionLogBySessionId={sessionLogBySessionId}
      />
      <DayEditModal 
         isOpen={!!dayEdit}
         onClose={() => setDayEdit(null)}
         dayKey={dayEdit?.dayKey}
         date={dayEdit?.date}
         tutors={sortedTutors}
         initialConfig={dayEditInitialConfig}
         initialAvailability={dayEditInitialAvailability}
         onApply={async (nextCfg) => {
             if (!dayEdit?.date || !dayEdit?.dayKey) return;
             if (!ensureDateIsEditable(dayEdit.date)) return;

             const d = dayEdit.date;
             const daySessions = sessions.filter((s) => isSameDay(new Date(s.start), d));

             if (nextCfg.isClosed && daySessions.length > 0) {
               const ok = window.confirm("Close this day? This will DELETE all sessions on this day.");
               if (!ok) return;
               await batchDeleteSessionsByIds(daySessions.map((s) => s.id));
             }

             const defaultRange = defaultTimelineRangeForDayKey(dayEdit.dayKey);
             const rawStart = Number(nextCfg.timelineStartMin);
             const rawEnd = Number(nextCfg.timelineEndMin);
             const timelineStartMin = Number.isFinite(rawStart) ? rawStart : defaultRange.startMin;
             const timelineEndMin =
               Number.isFinite(rawEnd) && rawEnd > timelineStartMin
                 ? rawEnd
                 : Math.max(timelineStartMin + 60, defaultRange.endMin);

             await upsertDayConfigByDate(d, {
               isClosed: !!nextCfg.isClosed,
               timelineStartMin,
               timelineEndMin,
             });

             const availabilityUpdates = Array.isArray(nextCfg?.availabilityUpdates) ? nextCfg.availabilityUpdates : [];
             if (availabilityUpdates.length > 0) {
               await Promise.all(
                 availabilityUpdates.map((av) =>
                   upsertAvailabilityByDate(d, av.uid, {
                     startMin: av.startMin,
                     endMin: av.endMin,
                     unavailable: av.unavailable,
                     hidden: av.hidden,
                   })
                 )
               );
             }

             setDayEdit(null);
             setRefreshKey((x) => x + 1);
         }}
      />
    </div>
  );
}
